import type { DriverActionKind } from "@storyboard/dispatch-view/fieldKinds";
import type { DriverRow } from "@/types/driver";

/** Duty statuses in which a driver is meant to hear and answer messages. */
const WORKING_DUTY_STATUSES: ReadonlySet<string> = new Set(["driving", "onDuty", "yardMove"]);

/** What a dispatcher reads about one action: what is wrong and why it matters, then the words to tell the driver. */
export type DriverActionGuidance = { summary: string; instruction: string };

/** The two reasons a driver is asked to open the app, each with its own explanation. */
const OPEN_APP_GUIDANCE = {
  notResponding: {
    summary:
      "The driver is driving, but their work device has not checked in for over 10 minutes. Storyboard cannot recover the app until it is reopened.",
    instruction: "Open Storyboard and tap Restart.",
  },
  handsFreeOff: {
    summary: "Hands-free is off, so messages do not play out loud and voice commands do not work.",
    instruction: "Bring Storyboard to the front on your work device so it can send and receive messages.",
  },
} as const satisfies Record<string, DriverActionGuidance>;

/** The dispatcher-facing explanation of every action whose wording never varies, keyed by kind. */
const FIXED_GUIDANCE: Record<Exclude<DriverActionKind, "open_app">, DriverActionGuidance> = {
  never_logged_in: {
    summary: "This driver has never logged into Storyboard.",
    instruction: "Log into Storyboard on your work-issued device.",
  },
  personal_device_only: {
    summary:
      "This driver is logged in only on a personal phone. Hands-free playback and voice commands run only on a work device.",
    instruction:
      "Log into Storyboard on your work-issued device. If you already have, change your device type to Work in settings.",
  },
  muted: {
    summary: "The work device is muted: messages play, but the driver cannot hear them.",
    instruction: "Turn up the volume on your work device so you can hear your messages.",
  },
};

/** What the wording of an action can depend on: whether the driver's device is checking in. */
export type DriverActionContext = Pick<DriverRow, "offline">;

/**
 * The dispatcher-facing explanation of one action: what is wrong, then what to tell the driver.
 *
 * The open-the-app action explains whichever reason applies, a device that stopped checking in or hands-free switched
 * off; every other action reads the same for every driver.
 *
 * @param action - The action to explain.
 * @param context - The driver's row, for whether its device is checking in.
 */
export function driverActionGuidance(action: DriverActionKind, context: DriverActionContext): DriverActionGuidance {
  if (action !== "open_app") return FIXED_GUIDANCE[action];
  // A device that is still checking in is open; the only reason left to send the driver back is hands-free.
  return context.offline === false ? OPEN_APP_GUIDANCE.handsFreeOff : OPEN_APP_GUIDANCE.notResponding;
}

/** The row signals {@link driverActionsFor} reads. */
export type DriverActionSignals = Pick<
  DriverRow,
  | "offline"
  | "mostRecentDutyStatusStatus"
  | "mostRecentDrivingStatus"
  | "mostRecentHandsFreeStatusEnabled"
  | "mostRecentVolumeAudible"
  | "volume"
>;

/** What the device-usage and never-logged-in polls say about a driver, beside the row's own signals. */
export type DriverDeviceFacts = {
  neverLoggedIn: boolean;
  personalDeviceOnly: boolean;
};

/**
 * The actions a driver owes before Storyboard works on their device, most pressing first; empty when nothing is wrong.
 *
 * A prototype of the server-side driver health conclusions, derived from the signals the Dispatch rows already carry.
 * Problems that only matter while the driver is meant to be listening (hands-free, volume, a silent device) are left
 * out off duty and in sleeper berth, so the column's filter lists only drivers a dispatcher should chase.
 *
 * @param row - The driver's merged row.
 * @param device - What the device-usage and never-logged-in polls say about the driver.
 */
export function driverActionsFor(row: DriverActionSignals, device: DriverDeviceFacts): DriverActionKind[] {
  // A fresh heartbeat proves a login, so it outranks the polled never-logged-in set, which lags a first login.
  const heartbeating = row.offline === false;
  if (device.neverLoggedIn && !heartbeating) return ["never_logged_in"];
  // A personal phone has no hands-free playback, so its hands-free and volume readings say nothing.
  if (device.personalDeviceOnly) return ["personal_device_only"];

  // A silent device matters only while the truck moves; off duty, or parked in a dead zone, it is expected.
  const driving = row.mostRecentDutyStatusStatus === "driving" || row.mostRecentDrivingStatus === "active";
  if (!heartbeating) return driving ? ["open_app"] : [];

  // Hands-free and volume count only while the driver is meant to be listening.
  const working = driving || WORKING_DUTY_STATUSES.has(row.mostRecentDutyStatusStatus ?? "");
  if (!working) return [];
  const actions: DriverActionKind[] = [];
  if (row.mostRecentHandsFreeStatusEnabled === false) actions.push("open_app");
  if (row.volume === 0 || row.mostRecentVolumeAudible === false) actions.push("muted");
  return actions;
}
